/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { Table } from 'src/core/components/Table';
import { SortKeyType } from 'src/core/components/TableProps';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { QnaAddSourceModalCommandBar } from 'src/qnaMaker/appShell/qnaMakerManageSourcesPage/QnaMakerAddSourceModalCommandBar';
import { QnaMakerSourceStore } from 'src/qnaMaker/core/stores/QnaMakerSourceStore';

export type QnaUrlSourceDto = { name: string; url: string; id: string; isUnstructured: boolean };
export type QnaAddUrlSourceModalProps = BasicModalProps<QnaUrlSourceDto[]>;

const messages = defineMessages({
    modalTitle: {
        id: 'qnaMaker.qnaAddUrlSourceModal.modalTitle',
        defaultMessage: 'Add URLs'
    },
    modalDescription: {
        id: 'qnaMaker.qnaAddUrlSourceModal.modalDescription',
        defaultMessage: 'You can add up to 10 URLs at a time. Question and answer pairs are extracted from each URL by default.'
    },
    addAll: {
        id: 'qnaMaker.qnaAddUrlSourceModal.addAll',
        defaultMessage: 'Add all'
    },
    urlName: {
        id: 'qnaMaker.qnaAddUrlSourceModal.urlName',
        defaultMessage: 'URL name'
    },
    sourceUrl: {
        id: 'qnaMaker.qnaAddUrlSourceModal.sourceUrl',
        defaultMessage: 'URL'
    },
    classifyStructure: {
        id: 'qnaMaker.qnaAddUrlSourceModal.classifyStructure',
        defaultMessage: 'Classify file structure'
    },
    autoDetect: {
        id: 'qnaMaker.qnaAddUrlSourceModal.autoDetect',
        defaultMessage: 'Auto-detect'
    },
    unstructured: {
        id: 'qnaMaker.qnaAddUrlSourceModal.unstructured',
        defaultMessage: 'Unstructured'
    }
});

const editableItemDefaultValue: QnaUrlSourceDto = { id: '', name: '', url: '', isUnstructured: false };

export const QnaAddUrlSourceModal = withModalMounter((props: QnaAddUrlSourceModalProps) => {
    const { onSubmit, onDismiss } = props;
    const { localize } = useLocalization();
    const [sortKey, setSortKey] = React.useState<SortKeyType<QnaMakerSourceStore>>({ key: 'name', order: 'desc' });

    const [sourceItems, setSourceItems] = React.useState<QnaUrlSourceDto[]>([]);
    const [newItemId] = React.useState(getId('newItem'));

    const [editableItem, setEditableItem] = useUpdateState<QnaUrlSourceDto>(editableItemDefaultValue);

    const { selection, selectedItems } = useSelection<QnaUrlSourceDto>();
    const isModalValid =
        (sourceItems.length || editableItem.id) &&
        (!editableItem.id || (editableItem.name.trim() && editableItem.url.trim())) &&
        sourceItems.every(s => s.id === editableItem.id || (s.name.trim() && s.url.trim()));

    React.useEffect(() => {
        setSourceItems(sources => orderBy(sources, [createKeyedStringNormalizer('name')], [sortKey.order]));
    }, [sortKey]);

    const handleSubmitNewItem = () => {
        if (editableItem.name && editableItem.url) {
            setSourceItems(sources =>
                sources.map(item => {
                    if (item.id === editableItem.id) {
                        return {
                            url: editableItem.url,
                            name: editableItem.name,
                            id: getId(),
                            isUnstructured: item.isUnstructured
                        };
                    }

                    return item;
                })
            );
            setEditableItem(editableItemDefaultValue);
            selection.setAllSelected(false);
        }
    };

    const handleAddSource = () => {
        const newItem = { id: newItemId, name: '', url: '', isUnstructured: false };
        setSourceItems(items => [newItem, ...items]);
        setEditableItem(newItem);
    };

    const handleDeleteSource = () => {
        setSourceItems(sources => sources.filter(item => !selectedItems.some(i => i.id === item.id)));
        selection.setAllSelected(false);
    };

    const handleRenameSource = () => {
        if (editableItem.id === newItemId) {
            setSourceItems(sources => sources.filter(item => item.id !== newItemId));
        }
        selection.setAllSelected(false);
        setEditableItem({ ...selectedItems[0] });
    };

    const handleClassifyFileStructure = (itemId: string, isUnstructured: boolean) => {
        setSourceItems(sources =>
            sources.map(item => {
                if (item.id === itemId) {
                    return { ...item, isUnstructured };
                }
                return item;
            })
        );
        selection.setAllSelected(false);
    };

    const onSortColumn = (col: IColumn) => setSortKey({ key: 'name', order: col.isSortedDescending ? 'asc' : 'desc' });

    const columns: IColumn[] = [
        {
            key: 'name',
            minWidth: 200,
            fieldName: 'name',
            isRowHeader: true,
            isResizable: true,
            name: localize(messages.urlName),
            isSorted: sortKey && sortKey.key === 'name',
            isSortedDescending: sortKey && sortKey.key === 'name' && sortKey.order === 'desc',

            onRender: (item: QnaUrlSourceDto) =>
                editableItem.id === item.id ? (
                    <LuisTextField
                        placeholder={localize(messages.urlName)}
                        data-automation-id="urlName"
                        onChange={(_, name) => {
                            setEditableItem({ name });
                        }}
                        autoFocus
                        value={editableItem.name}
                        onBlur={handleSubmitNewItem}
                    />
                ) : (
                    <TruncatedText title={item.name}>{item.name}</TruncatedText>
                )
        },
        {
            key: 'url',
            minWidth: 300,
            fieldName: 'url',
            isRowHeader: true,
            isResizable: true,
            name: localize(messages.sourceUrl),
            onRender: (item: QnaUrlSourceDto) =>
                editableItem.id === item.id && item.id === newItemId ? (
                    <LuisTextField
                        placeholder={localize(messages.sourceUrl)}
                        data-automation-id="urlLink"
                        onChange={(_, url) => {
                            setEditableItem({ url });
                        }}
                        styles={{ root: { minWidth: 300 } }}
                        onBlur={handleSubmitNewItem}
                        value={editableItem.url}
                    />
                ) : (
                    <TruncatedText title={item.url}>{item.url}</TruncatedText>
                )
        },
        {
            key: 'classifyStructure',
            minWidth: 200,
            fieldName: 'classifyStructure',
            isRowHeader: true,
            isResizable: true,
            name: localize(messages.classifyStructure),
            onRender: (item: QnaUrlSourceDto) => (
                <LuisDropdown
                    options={[
                        { key: 'autoDetect', text: localize(messages.autoDetect) },
                        { key: 'unstructured', text: localize(messages.unstructured) }
                    ]}
                    isSearchEnabled={false}
                    selectedKey={item.isUnstructured ? 'unstructured' : 'autoDetect'}
                    onChange={(_, i) => handleClassifyFileStructure(item.id, i.key === 'unstructured')}
                />
            )
        }
    ];

    return (
        <BasicModal
            {...props}
            doneButtonText={localize(messages.addAll)}
            onSubmit={() => onSubmit(sourceItems)}
            onDismiss={onDismiss}
            valid={isModalValid}
            dialogContentProps={{
                ...props.dialogContentProps,
                title: localize(messages.modalTitle),
                subText: localize(messages.modalDescription)
            }}
            minWidth={950}>
            <Stack styles={{ root: { minHeight: 250 } }} verticalFill>
                <QnaAddSourceModalCommandBar
                    sourceKind="url"
                    isAddDisabled={Boolean(editableItem.id)}
                    selectedItems={selectedItems}
                    onAddSources={handleAddSource}
                    onDeleteSource={handleDeleteSource}
                    onRenameSource={handleRenameSource}
                />
                <Table<QnaUrlSourceDto> items={sourceItems} columns={columns} selection={selection} onSortColumn={onSortColumn} />
            </Stack>
        </BasicModal>
    );
});
